import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Select, Spin, Typography } from "antd";
import { ensureGoogleMapsPlacesLoaded } from "utils/googleMapsLoader";

const GooglePlacesTagsSelect = ({
  googleMapsApiKey,
  value,
  onChange,
  placeholder = "Search places...",
  types,
  disabled,
  resolveValueFromDetails,
  tokenSeparators,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);
  const placesContainerRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const [options, setOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);
  const currentValueRef = useRef([]);

  useEffect(() => {
    let alive = true;
    setLoadError(null);
    setIsLoaded(false);
    ensureGoogleMapsPlacesLoaded(googleMapsApiKey)
      .then((ok) => {
        if (!alive) return;
        setIsLoaded(Boolean(ok));
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(err);
        setIsLoaded(false);
      });
    return () => {
      alive = false;
    };
  }, [googleMapsApiKey]);

  useEffect(() => {
    if (!isLoaded || !googleMapsApiKey) return;
    if (!window.google?.maps?.places) return;

    if (!autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
    }

    if (!placesContainerRef.current) {
      placesContainerRef.current = document.createElement("div");
    }

    if (!placesServiceRef.current) {
      placesServiceRef.current = new window.google.maps.places.PlacesService(
        placesContainerRef.current,
      );
    }
  }, [isLoaded, googleMapsApiKey]);

  const effectiveDisabled = disabled || !googleMapsApiKey || Boolean(loadError) || !isLoaded;

  const help = useMemo(() => {
    if (loadError) return "Google Maps failed to load.";
    if (!googleMapsApiKey) return "Google Maps API key not available.";
    if (!isLoaded) return "Loading Google Maps...";
    return null;
  }, [googleMapsApiKey, isLoaded, loadError]);

  const getDetails = useCallback(
    (placeId) =>
      new Promise((resolve, reject) => {
        const svc = placesServiceRef.current;
        if (!svc || !placeId) return reject(new Error("PlacesService not ready"));
        svc.getDetails(
          { placeId, fields: ["address_components", "formatted_address", "place_id"] },
          (result, status) => {
            if (status !== window.google.maps.places.PlacesServiceStatus.OK || !result) {
              reject(new Error(`Place details failed: ${status || "unknown"}`));
              return;
            }
            resolve(result);
          },
        );
      }),
    [],
  );

  const onSearch = useCallback(
    (text) => {
      const trimmed = String(text || "").trim();
      const svc = autocompleteServiceRef.current;
      if (effectiveDisabled || !svc || trimmed.length < 2) {
        setOptions([]);
        return;
      }

      const seq = ++fetchSeqRef.current;
      setSearchLoading(true);
      svc.getPlacePredictions(
        { input: trimmed, types: Array.isArray(types) ? types : undefined },
        (predictions) => {
          if (seq !== fetchSeqRef.current) return;
          const safe = Array.isArray(predictions) ? predictions : [];
          setOptions(
            safe.map((p) => ({
              label: p.description,
              value: p.description,
              placeId: p.place_id,
            })),
          );
          setSearchLoading(false);
        },
      );
    },
    [effectiveDisabled, types],
  );

  const normalizeList = useCallback(
    (list) =>
      (Array.isArray(list) ? list : [])
        .map((v) => String(v || "").trim())
        .filter(Boolean)
        .filter((v, idx, all) => all.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === idx),
    [],
  );

  const onInternalChange = useCallback(
    (nextValue) => {
      if (!onChange) return;
      const safe = normalizeList(nextValue);
      currentValueRef.current = safe;
      onChange(safe);
    },
    [normalizeList, onChange],
  );

  const onSelect = useCallback(
    async (selectedValue, option) => {
      const placeId = option?.placeId;
      if (!placeId || typeof resolveValueFromDetails !== "function") return;

      setSelecting(true);
      try {
        const details = await getDetails(placeId);
         const resolved = resolveValueFromDetails(details);
        if (resolved && String(resolved) !== String(selectedValue)) {
          const raw = String(selectedValue);
          const resolvedStr = String(resolved);
          if (onChange) {
            const current = currentValueRef.current;
            const updated = current.map((v) => (v === raw ? resolvedStr : v));
            currentValueRef.current = normalizeList(updated);
            onChange(currentValueRef.current);
          }
        }
      } catch {
        // keep raw selection
      } finally {
        setSelecting(false);
      }
    },
    [getDetails, resolveValueFromDetails, normalizeList, onChange],
  );

  return (
    <div>
      <Select
        mode="tags"
        tokenSeparators={tokenSeparators}
        placeholder={placeholder}
        disabled={effectiveDisabled}
        options={options}
        filterOption={false}
        onSearch={onSearch}
        onSelect={onSelect}
        value={value}
        onChange={onInternalChange}
        loading={searchLoading}
        style={{ width: "100%" }}
        notFoundContent={searchLoading ? <Spin size="small" /> : null}
        suffixIcon={selecting ? <Spin size="small" /> : undefined}
      />
      {help && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {help}
        </Typography.Text>
      )}
    </div>
  );
};

export default GooglePlacesTagsSelect;
